import type { CSSProperties } from 'react'

/**
 * Restaurant accent color (T14): the restaurant picks any hex; we derive colors that are
 * guaranteed readable. Accents style fills, borders and text — never full backgrounds.
 */

export const DEFAULT_ACCENT = '#c2410c'

/** Text colors placed on an accent fill; the light one matches `--fg`, the dark one dark `--fg`. */
const DARK_TEXT = '#171717'
const LIGHT_TEXT = '#f4f4f2'
/** Neutral surfaces the accent sits on; must match `--surface` in styles.css per theme. */
const SURFACES = { light: '#ffffff', dark: '#1e1e1c' }

/** WCAG AA for normal text; also used for fills so labels always read. */
const MIN_TEXT_CONTRAST = 4.5
/** WCAG AA for UI components; a fill must at least stand out from its surface. */
const MIN_FILL_CONTRAST = 3
/** Per adjustment step, so the hue drifts as little as possible. */
const STEP = 0.04

export interface Accent {
  /** Fill for primary buttons, active nav items, badges. */
  fill: string
  /** Text/icon color on `fill`. */
  onFill: string
  /** The accent used as text, links, borders on the neutral surface. */
  ink: string
}

export type AccentTheme = keyof typeof SURFACES

type Rgb = readonly [number, number, number]

export function parseHex(input: string): Rgb | undefined {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(input.trim())
  if (!m) return undefined
  const hex = m[1]!
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as unknown as Rgb
}

export function toHex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')
}

function channel(c: number): number {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance, 0 (black) … 1 (white). */
export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio, 1 … 21. */
export function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a)
  const lb = luminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

/** Mixes toward `toward` (black or white) until `against` reads on it; the endpoint always passes. */
function adjustUntil(color: Rgb, against: Rgb, min: number, toward: 0 | 255): Rgb {
  let c = color
  for (let i = 0; contrast(c, against) < min && i < 100; i++) {
    c = c.map((v) => v + (toward - v) * STEP) as unknown as Rgb
  }
  return c
}

const WHITE = parseHex(LIGHT_TEXT)!
const DARK = parseHex(DARK_TEXT)!

/** Any input → readable colors on the given theme; an invalid hex falls back to the default. */
export function resolveAccent(input: string | null | undefined, theme: AccentTheme = 'light'): Accent {
  const rgb = (input && parseHex(input)) || parseHex(DEFAULT_ACCENT)!
  const surface = parseHex(SURFACES[theme])!
  const away: 0 | 255 = theme === 'light' ? 0 : 255

  // The fill must stand out from the surface (a navy accent on a dark theme is invisible).
  let fill = adjustUntil(rgb, surface, MIN_FILL_CONTRAST, away)

  // Text on the fill: light if it reads, else dark; a mid-tone that suits neither is
  // pushed away from the surface until light text does.
  let onFill = WHITE
  if (contrast(fill, WHITE) < MIN_TEXT_CONTRAST) {
    if (contrast(fill, DARK) >= MIN_TEXT_CONTRAST) onFill = DARK
    else if (away === 0) fill = adjustUntil(fill, WHITE, MIN_TEXT_CONTRAST, 0)
    else {
      onFill = DARK
      fill = adjustUntil(fill, DARK, MIN_TEXT_CONTRAST, 255)
    }
  }

  // As text/border on the surface the bar is higher than for a fill.
  const ink = adjustUntil(rgb, surface, MIN_TEXT_CONTRAST, away)

  return { fill: toHex(fill), onFill: toHex(onFill), ink: toHex(ink) }
}

/**
 * Inline-style vars for both themes; styles.css picks the set for the active
 * `<html data-theme>`, so a theme switch needs no re-render.
 */
export function accentStyle(color: string | null | undefined): CSSProperties {
  const light = resolveAccent(color, 'light')
  const dark = resolveAccent(color, 'dark')
  return {
    '--accent-light': light.fill,
    '--accent-fg-light': light.onFill,
    '--accent-ink-light': light.ink,
    '--accent-dark': dark.fill,
    '--accent-fg-dark': dark.onFill,
    '--accent-ink-dark': dark.ink,
  } as CSSProperties
}
